/**
 * ConfirmDialog — modal de confirmación con el design system del
 * proyecto (no `window.confirm` nativo, que es feo y no respeta la
 * paleta de marca, tipografía, ni el tono del producto).
 *
 * Patrón de uso (controlled, sin promise wrapping para mantener el
 * componente dumb):
 *
 *   const [confirmOpen, setConfirmOpen] = useState(false)
 *   ...
 *   <button onClick={() => setConfirmOpen(true)}>Aprobar</button>
 *   <ConfirmDialog
 *     open={confirmOpen}
 *     tone="positive"
 *     title={t('approve.title')}
 *     body={t('approve.body')}
 *     confirmLabel={t('approve.confirm')}
 *     cancelLabel={t('approve.cancel')}
 *     onCancel={() => setConfirmOpen(false)}
 *     onConfirm={async () => {
 *       await mutation.mutateAsync()
 *       setConfirmOpen(false)
 *     }}
 *   />
 *
 * Tonos disponibles:
 *   - 'neutral' : info gris (default).
 *   - 'positive': verde (oficial / aprobaciones / confirmaciones
 *     "todo bien").
 *   - 'warning' : amarillo/naranja (review — atención requerida).
 *   - 'danger'  : rojo (rechazo, eliminación, acciones destructivas).
 *
 * El botón de confirm refleja el tono. Cancel siempre es neutral.
 *
 * Accesibilidad:
 *   - role="alertdialog" (es una decisión, no solo info).
 *   - ESC cierra (vía onCancel).
 *   - Focus inicial en el botón cancel para evitar Enter accidental
 *     en el confirm.
 *   - aria-labelledby al título.
 */

import { useEffect, useRef } from 'react'
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  XCircle,
} from 'lucide-react'

export type ConfirmTone = 'neutral' | 'positive' | 'warning' | 'danger'

export interface ConfirmDialogProps {
  open: boolean
  tone?: ConfirmTone
  title: string
  /** Cuerpo del modal. Puede ser string o ReactNode para texto rico. */
  body: React.ReactNode
  confirmLabel: string
  cancelLabel: string
  /** Si true, deshabilita ambos botones y muestra spinner en confirm. */
  busy?: boolean
  onCancel: () => void
  onConfirm: () => void | Promise<void>
}

const TONE_STYLES: Record<
  ConfirmTone,
  {
    icon: React.ReactNode
    iconBgClass: string
    confirmClass: string
  }
> = {
  neutral: {
    icon: <Info className="w-5 h-5" />,
    iconBgClass: 'bg-surface-muted text-ink-muted',
    confirmClass: 'bg-brand hover:bg-brand-deep text-canvas',
  },
  positive: {
    icon: <CheckCircle2 className="w-5 h-5" />,
    iconBgClass: 'bg-official-soft text-official',
    confirmClass: 'bg-official hover:bg-official-deep text-canvas',
  },
  warning: {
    icon: <AlertTriangle className="w-5 h-5" />,
    iconBgClass: 'bg-review-soft text-review',
    confirmClass: 'bg-review hover:bg-review-deep text-canvas',
  },
  danger: {
    icon: <XCircle className="w-5 h-5" />,
    iconBgClass: 'bg-critical-soft text-critical',
    confirmClass: 'bg-critical hover:bg-critical-deep text-canvas',
  },
}

export function ConfirmDialog({
  open,
  tone = 'neutral',
  title,
  body,
  confirmLabel,
  cancelLabel,
  busy = false,
  onCancel,
  onConfirm,
}: ConfirmDialogProps) {
  const cancelButtonRef = useRef<HTMLButtonElement>(null)

  // Focus inicial en cancel (evita Enter accidental en confirm).
  useEffect(() => {
    if (open && cancelButtonRef.current) {
      cancelButtonRef.current.focus()
    }
  }, [open])

  // ESC cierra (cuando no está ocupado).
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, busy, onCancel])

  if (!open) return null

  const toneStyle = TONE_STYLES[tone]

  function handleConfirmClick() {
    const result = onConfirm()
    if (result instanceof Promise) {
      // El caller maneja el estado busy via prop. Aquí solo no hacemos
      // nada extra — la promesa la atrapa el caller.
      void result
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel()
      }}
    >
      <div className="mx-4 w-full max-w-md rounded-md border border-line bg-surface shadow-xl">
        <div className="px-5 py-5">
          <div className="flex items-start gap-3">
            <div
              className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${toneStyle.iconBgClass}`}
              aria-hidden="true"
            >
              {toneStyle.icon}
            </div>
            <div className="flex-1 min-w-0">
              <h2
                id="confirm-dialog-title"
                className="font-display text-base font-semibold text-ink"
              >
                {title}
              </h2>
              <div className="mt-2 text-sm text-ink-muted whitespace-pre-line break-words">
                {body}
              </div>
            </div>
          </div>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-line bg-canvas/40 px-5 py-3 rounded-b-md">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-1.5 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirmClick}
            disabled={busy}
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-md disabled:opacity-50 ${toneStyle.confirmClass}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            {confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  )
}

// Helper for callers that just want a tone-mapped icon outside the
// dialog (e.g., toast variants).
export const CONFIRM_ICON_BY_TONE: Record<ConfirmTone, React.ReactNode> = {
  neutral: <Info className="w-5 h-5" />,
  positive: <CheckCircle2 className="w-5 h-5" />,
  warning: <AlertTriangle className="w-5 h-5" />,
  danger: <AlertCircle className="w-5 h-5" />,
}
